<template>
  <private-gym-page id="abonnement-page" name="abonnement" :has_fab="false">

    <f7-block-title medium>
      Abonnement
    </f7-block-title>

    <f7-block-header>
      Al je abonnementen en betalingen, het nieuwste bovenaan.
    </f7-block-header>

    <!-- The inline preloader from reserveringen.vue and notificaties.vue,
         deliberately NOT the `app` store's global loader overlay that
         payments.vue uses: that overlay covers the navbar and swallows the back
         gesture, so a member who opened this screen by accident has nothing left
         to press. -->
    <f7-block v-if="show_preloader" class="text-align-center">
      <f7-preloader></f7-preloader>
    </f7-block>

    <!-- Persistent rather than a toast, for the same reason notificaties.vue
         gives: a load failure leaves the member with nothing on screen to act
         on, so the message must not disappear on a timer. No raw API text is
         surfaced -- it is English, unbounded, and meaningless to a member. -->
    <f7-block v-else-if="load_failed" strong inset>
      <div>We konden je abonnement niet laden.</div>
      <div class="mt-2">Controleer je internetverbinding en probeer het opnieuw.</div>
      <f7-button fill class="mt-3" @click="load">Opnieuw proberen</f7-button>
    </f7-block>

    <!--
      EVERY subscription, active or not, newest first, and the newest one's
      Betalingen accordion already open (owner decision, 2026-08-20; F17).

      `:expanded="index === 0"` and nothing cleverer. The page does not remember
      which row the member last opened and does not open more than one: a member
      arriving from a payment notification is here for the most recent charge,
      and every card popping open would bury it again in a different way.
    -->
    <template v-else-if="subscriptions.length">
      <user-subscription-details
        v-for="(subscription, index) in subscriptions"
        :key="subscription.id"
        :subscription="subscription"
        :user="user"
        :expanded="index === 0"
      ></user-subscription-details>
    </template>

    <!--
      The one place in this app where the till IS the right answer. Everywhere
      else in phase 40 it is the wrong destination -- /account/payments is seven
      buy buttons and no history (GATE-40-B) -- but somebody with no
      subscription at all cannot be sent to a statement, because they have none.
    -->
    <f7-block v-else strong inset>
      <div>Je hebt nog geen abonnement.</div>
      <div class="mt-2">
        <f7-link href="/account/payments">Naar Betaling</f7-link>
      </div>
    </f7-block>

  </private-gym-page>
</template>

<script>

import PrivateGymPage from "../_layouts/PrivateGymPage.vue";
import UserSubscriptionDetails from "../../components/account/users/UserSubscriptionDetails.vue";

/**
 * /account/abonnement -- a member's own subscriptions and payments (NOTIF-06).
 *
 * ============================================================================
 *  WHY THIS PAGE EXISTS, AND WHY IT IS A SECOND VIEW OF DATA THAT ALREADY SHOWS
 * ============================================================================
 *
 * 40-04 un-gated the Betalingen block on /users/:user_id. 40-05 pointed the
 * payment notifications at it. 40-06 delivered one to a real handset, the owner
 * tapped it, and it opened a page called "Instellingen" on which the failed
 * payment sat inside a collapsed accordion -- recorded as finding F17.
 *
 * The owner's decision, 2026-08-20, verbatim: "Add an 'Abonnement' page that has
 * all subscriptions (active or not) listed in reverse chronological order,
 * newest at the top, with the newest one having the Betalingen accordion
 * expanded. Then we show that data on 2 pages, 'Instellingen' and 'Abonnement',
 * and that's more than fine."
 *
 * So the duplication is deliberate and signed off. Both screens render the SAME
 * component against the SAME response; this page differs only in what it is
 * called, what it leads with, and which accordion it opens.
 *
 * ============================================================================
 *  NO NEW ENDPOINT, AND THE ID IS NOT A PARAMETER
 * ============================================================================
 *
 * This fetches `/users/{id}` -- exactly what UserId.vue fetches, exactly what
 * 40-04 proved the API already serves a member for their own record
 * (UserFullResource:57 -> SubscriptionResource:39 -> PaymentResource::collection,
 * gated by UserPolicy::view(), which for a member is `$authUser->id === $user->id`).
 * No API change was needed and none was made.
 *
 * THE ID COMES FROM THE SESSION AND THERE IS NOWHERE ELSE IT COULD COME FROM
 * (T-40-09-03). This page has no route parameter to read and no payload reaches
 * it: `betalingen` now resolves to a FIXED path, so a push cannot name a member
 * even in principle. With no id in the session nothing is fetched at all --
 * `/users/undefined` is a request this app must not make, and a tap left pending
 * across a logout now lands here rather than on home.
 */
export default {
  components: { PrivateGymPage, UserSubscriptionDetails },

  props: {
    f7route: Object,
    f7router: Object,
  },

  mixins: [],

  data: function () {
    return {
      // The whole `/users/{id}` record, because the card needs `user` too --
      // `discount_percentage` is what turns the subscription's stored amount
      // into the amount this member is actually charged.
      user: null,

      // A GET is in flight. Starts true because mounted() always issues one (or
      // decides not to and clears it), so the first render is the preloader
      // rather than a flash of the empty state at somebody who has three
      // subscriptions.
      is_fetching: true,

      load_failed: false,
    };
  },

  computed: {
    current_user() {
      return this.$store.state.current_user.data;
    },

    /**
     * Every subscription, newest first.
     *
     * ORDERED HERE RATHER THAN ASKED FOR IN ORDER. `SubscriptionResource` has no
     * ordering contract -- `$user->subscriptions` arrives in whatever order the
     * relation returns -- and adding an `orderBy` to the API would change what
     * /users/:user_id renders for admins as a side effect of a member-facing
     * page. Sorting a handful of rows in the client costs nothing.
     *
     * `created_at` is the key, not `id`: "newest" is a claim about a date, and
     * the two can disagree once rows are ever backfilled or re-imported.
     * `created_at` IS nullable on these tables, though -- finding F8 records a
     * live NULL dereference on the payments side -- and `Date.parse(null)` is
     * NaN, which would make the comparator return NaN and leave the order
     * undefined rather than merely wrong. So an unparseable date falls back to
     * `id` descending, and equal dates break the tie the same way, which also
     * makes the sort deterministic instead of engine-dependent.
     *
     * @returns {Array<Object>}
     */
    subscriptions() {
      const list = this.user && Array.isArray(this.user.subscriptions)
        ? this.user.subscriptions
        : [];

      // Copied before sorting: Array.prototype.sort mutates, and `this.user` is
      // the response object the rest of the page reads.
      return [...list].sort((a, b) => {
        const byId = (b.id || 0) - (a.id || 0);
        const aTime = Date.parse(a.created_at);
        const bTime = Date.parse(b.created_at);

        if (Number.isNaN(aTime) || Number.isNaN(bTime) || aTime === bTime) {
          return byId;
        }

        return bTime - aTime;
      });
    },

    /**
     * The loading block exists only while there is nothing else to show. Keyed
     * off `user` rather than off the subscription count, so a member with zero
     * subscriptions sees the empty state once the answer is in, instead of a
     * spinner that never resolves into anything.
     */
    show_preloader() {
      return this.is_fetching && this.user === null;
    },
  },

  created() {
    //
  },

  mounted() {
    // In mounted() rather than created() so the page chrome and the preloader
    // render first -- created() runs before the first paint, so a
    // synchronously-resolving fetch there could skip the loading state entirely.
    this.load();
  },

  methods: {
    load() {
      const id = this.current_user ? this.current_user.id : null;

      // No session, no request. See the header: this is the T-40-09-03
      // mitigation, and it is also what stops a logged-out deep link firing
      // `/users/undefined` at the API.
      if (!id) {
        this.is_fetching = false;

        return;
      }

      this.is_fetching = true;
      this.load_failed = false;

      this.$store
        .dispatch('api/getEndpoint', { endpoint: '/users/' + id })
        .then((response) => {
          this.user = response.data.data;
        })
        .catch(() => {
          this.load_failed = true;
        })
        .finally(() => {
          this.is_fetching = false;
        });
    },
  },

  watch: {
    //
  }
};
</script>
